import { consume } from '@lit/context';
import { LitElement, css, html, nothing, PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { formDataContext } from '../../contexts/formData';
import { makeCustomEvent, EVENT } from '../../util/events';
import { MAX_DEPENDENTS, MAX_MEMBERS, RateQuoteFormData, RateQuoteMember, RELATION } from '../../util/form';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { ref } from 'lit/directives/ref.js';
import { isValidZipcode } from '../../util/zipcode';
import { getZipcodeInfo } from '../../controllers/zipcounty';
import { formatDate, isOver65Years, isUnder26Years, isValidBirthday, isValidDate, MIN_DATE } from '../../util/date';
import { breakpointContext } from '../../contexts/breakpoint';
import { BREAKPOINT } from '../../util/breakpoints';
const HELP_PHONE_NUMBER = '1-800-894-2880';

const MEDICARE_URL = `${import.meta.env.VITE_AWS_URL}/medicare-plan-finder/index.html?#/main`;

@customElement('rq-member-card')
export class RateQuoteEstimate extends LitElement {
    static styles = css`
        .wrapper {
            display: flex;
            flex-direction: column;
            padding: 24px;
            gap: 12px;
            border-radius: 8px;
            border: 1px solid var(--lh-color-neutral-500, #919799);
        }
        .inputs {
            display: flex;
            flex-direction: column;
            gap: 24px;
        }
        .disabled,
        .loader-label {
            color: var(--lh-color-neutral-500, #919799);
        }
        .county-loader {
            display: flex;
            flex-direction: column;
            gap: 12px;
        }
        .title {
            display: flex;
            flex-direction: row;
            justify-content: space-between;
        }
        .dob-warning,
        .zip-warning {
            display: flex;
            flex-direction: row;
            gap: 16px;
            border-radius: 8px;
            padding: 24px;
        }
        .zip-warning {
            background: var(--lh-color-warning-500, #f9e719);
        }
        .dob-warning {
            background: var(--lh-color-primary-300, #5ecfff);
        }
        .icon-div {
            margin: auto;
        }
    `;

    connectedCallback() {
        super.connectedCallback();
        this.addEventListener('lh-change', this.listenerHandler as unknown as EventListener);
        this.addEventListener('lh-input', this.listenerHandler as unknown as EventListener);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('lh-change', this.listenerHandler as unknown as EventListener);
        this.removeEventListener('lh-input', this.listenerHandler as unknown as EventListener);
    }

    updated(changedProperties: PropertyValues<this>) {
        super.updated(changedProperties);
        // lh-input type="dob" default-value doesn't apply masking; use change() after render.
        // `data` comes from @consume without @property/@state, so it never shows up in
        // changedProperties — track the last synced dob instead of relying on that map.
        const dob = this.member?.dob;
        if (dob && dob !== this.lastSyncedDob) {
            this.lastSyncedDob = dob;
            setTimeout(() => {
                if (!this.isConnected) return;
                const dobInput = this.shadowRoot?.querySelector('lh-input[name="dob"]') as {
                    change?: (value: string) => void;
                } | null;
                dobInput?.change?.(this.formatDateToMMDDYYYY(dob));
            }, 0);
        }
    }

    @consume({ context: formDataContext, subscribe: true })
    data!: RateQuoteFormData;

    get primary() {
        return this.idx === 0;
    }

    get lastMember() {
        return this.idx === MAX_MEMBERS - 1;
    }

    @property({ type: Number })
    idx!: number;

    private lastSyncedDob?: string;

    @state()
    searching: boolean = false;

    @state()
    zipError = '';

    @state()
    countyError = '';

    @state()
    allCounties: {
        county_fips?: string;
        county_name: string;
        state_abbreviation: string;
        state: string;
    }[] = [];

    @state()
    toast: {
        show: boolean;
        message?: string;
    } = { show: false };

    @consume({ context: breakpointContext, subscribe: true })
    breakpoint!: BREAKPOINT;

    get isMobile() {
        return this.breakpoint === BREAKPOINT.PHONE || this.breakpoint === BREAKPOINT.TABLET;
    }

    private syncRadioGroup = (el: Element | undefined) => {
        if (!el || !this.member?.relation) return;
        const relation = this.member.relation;
        customElements.whenDefined('lh-radio-group').then(() => {
            const rg = el as HTMLElement & {
                value?: string;
                change?: (v: string) => void;
                updateComplete?: Promise<boolean>;
            };
            const doSync = () => {
                if (rg.change && rg.value !== relation) {
                    rg.change(relation);
                }
            };
            if (rg.updateComplete) {
                rg.updateComplete.then(doSync);
            } else {
                doSync();
            }
        });
    };

    private async listenerHandler({
        detail: { name, value },
    }: CustomEvent<{ name: keyof RateQuoteMember; value: unknown }>) {
        const members = [...this.data.members];
        if (!this.member) return;
        let member = { ...this.member };
        if (name === 'dob') {
            if (isValidDate(value as string)) {
                member = { ...member, dob: value as string };
            }
        } else {
            member = { ...member, [name]: value };
        }
        if (name === 'county') {
            const val: { county_name: string; state_abbreviation: string } = value as {
                county_name: string;
                state_abbreviation: string;
            };
            member = { ...member, county: val?.county_name, state: val?.state_abbreviation };
        }

        members[this.idx] = member;
        if (name === 'relation' && value === RELATION.SPOUSE) {
            members.forEach((m, i) => {
                if (i !== this.idx && i !== 0) {
                    members[i] = { ...m, relation: RELATION.DEPENDENT };
                }
            });
        }
        if (name === 'zipcode') {
            this.zipError = '';
            this.countyError = '';
            if (!isValidZipcode(value as string)) {
                delete members[this.idx].state;
                delete members[this.idx].county;
                this.allCounties = [];
            } else if (value !== this.member.zipcode) {
                this.updateStateInfo(value as string);
            }
        }
        const updatedForm: RateQuoteFormData = { ...this.data, members };
        this.dispatchEvent(makeCustomEvent(EVENT.UPDATE_FORM, { data: updatedForm }));
    }

    get member() {
        return this.data.members[this.idx];
    }

    get memberTitle() {
        if (this.primary) {
            return 'Primary member';
        }
        return `Additional member`;
    }

    get relationInput() {
        if (this.primary) return nothing;
        const relation = this.member.relation;
        const spouseExists = this.data.members.some(
            (m, i) => i !== this.idx && (m as RateQuoteMember).relation === RELATION.SPOUSE
        );
        const dependentCount = this.data.members.filter(
            (m) => (m as RateQuoteMember).relation === RELATION.DEPENDENT
        ).length;
        const isSpouse = relation === RELATION.SPOUSE;
        const disabled = spouseExists && !isSpouse;
        const disableDependent = Boolean((!relation || isSpouse) && dependentCount >= MAX_DEPENDENTS);

        return html`<lh-radio-group
            ${ref(this.syncRadioGroup)}
            required
            name="relation"
            default-value=${ifDefined(relation)}
        >
            <lh-group-legend slot="legend">Relationship to primary member</lh-group-legend>
            <lh-radio
                disabled=${Boolean(spouseExists && !isSpouse) || nothing}
                value=${RELATION.SPOUSE}
                class=${classMap({ disabled })}
                @click=${(e: MouseEvent) => {
                    if (disabled) {
                        e.preventDefault();
                    }
                }}
                >Spouse or domestic partner</lh-radio
            >
            <lh-radio
                disabled=${disableDependent || nothing}
                value=${RELATION.DEPENDENT}
                class=${classMap({ disabled: disableDependent })}
                @click=${(e: MouseEvent) => {
                    if (disableDependent) {
                        e.preventDefault();
                    }
                }}
                >Dependent</lh-radio
            >
        </lh-radio-group>`;
    }

    get memberDob() {
        if (!this.member.dob) return nothing;
        return formatDate(this.member.dob);
    }

    private formatDateToMMDDYYYY(date: string): string {
        // Convert from YYYY-MM-DD to MM/DD/YYYY
        if (!date) return '';
        if (date.includes('/')) return date; // Already in correct format

        const parts = date.split('-');
        if (parts.length === 3) {
            return `${parts[1]}/${parts[2]}/${parts[0]}`;
        }
        return date;
    }

    get dobInput() {
        // lh-input type="dob" default-value must be MM/DD/YYYY, not ISO
        const defaultValue = this.member.dob ? this.formatDateToMMDDYYYY(this.member.dob) : '';
        return html`<lh-input
            required
            type="dob"
            name="dob"
            default-value=${defaultValue}
            min=${formatDate(MIN_DATE, 'input')}
            max=${formatDate(new Date(), 'input')}
        >
            Date of birth
        </lh-input>`;
    }

    get zipErrorMessage() {
        if (this.member.zipcode && !isValidZipcode(this.member.zipcode)) {
            return 'Please enter a valid 5-digit ZIP code';
        }
        return this.zipError || nothing;
    }

    get zipInput() {
        if (!this.primary) return nothing;
        return html`<lh-input
            required
            name="zipcode"
            max-count="5"
            default-value=${ifDefined(this.member.zipcode)}
            error-message=${this.zipErrorMessage}
            >North Carolina Zip Code</lh-input
        >`;
    }

    private async updateStateInfo(zipcode: string) {
        this.searching = true;
        this.zipError = '';
        this.countyError = '';
        try {
            const county = await getZipcodeInfo(zipcode);
            const { county_name, state_abbreviation, alternate_counties, state } = county?.[0]?.zipcodes?.[0] || {};
            const detectedState = state_abbreviation || '';

            // Only include NC counties (matches medicare-rate-quote shopper behavior)
            let allCounties =
                county_name && state_abbreviation && state && state_abbreviation.toLowerCase() === 'nc'
                    ? [{ county_name, state_abbreviation, state }]
                    : [];
            if (alternate_counties && alternate_counties.length) {
                allCounties = allCounties.concat(
                    alternate_counties?.filter((c) => c.state_abbreviation.toLowerCase() === 'nc')
                );
            }
            this.allCounties = allCounties;

            if (allCounties.length === 0) {
                if (detectedState && detectedState.toLowerCase() !== 'nc') {
                    this.zipError = 'Enter valid NC zip code.';
                    this.countyError = 'This ZIP Code is not in North Carolina.';
                } else {
                    this.zipError = 'Invalid Zip Code';
                }
                const members = [...this.data.members];
                members[this.idx] = { ...this.member, county: undefined, state: detectedState || undefined };
                this.dispatchEvent(makeCustomEvent(EVENT.UPDATE_FORM, { data: { ...this.data, members } }));
            } else if (allCounties.length === 1) {
                const member = { ...this.member, county: county_name, state: state_abbreviation };
                const members = [...this.data.members];
                members[this.idx] = member;
                this.dispatchEvent(makeCustomEvent(EVENT.UPDATE_FORM, { data: { ...this.data, members } }));
            }
        } catch (e) {
            console.error(e);
            this.countyError = 'Unable to validate ZIP Code right now. Please try again.';
        } finally {
            this.searching = false;
        }
    }

    get errorToast() {
        if (!this.toast.show) return nothing;
        return html`<lh-toast
            variant="error"
            shade="color"
            header="Error getting zipcode"
            description=${this.toast.message || nothing}
            position="top"
            has-icon
            open
        ></lh-toast>`;
    }

    get countyInput() {
        if (!this.primary) return nothing;
        if (this.searching) {
            return html`<div class="county-loader">
                <lh-typography size="sm"><span class="loader-label"> North Carolina county </span></lh-typography>
                <lh-loader size="sm"></lh-loader>
            </div>`;
        }
        if (!this.allCounties.length || this.allCounties.length === 1) {
            const val = this.member.county ? this.member.county : nothing;
            return html`<lh-input
                required
                name="county"
                disabled
                default-value=${val}
                value=${val}
                error-message=${this.countyError || nothing}
            >
                North Carolina county
            </lh-input>`;
        } else {
            return html`<lh-select
                required
                name="county"
                default-value=${ifDefined(this.member.county)}
                value=${ifDefined(this.member.county)}
            >
                North Carolina county
                <lh-option value slot="options">Select One</lh-option>
                ${this.allCounties.map((c) => html`<lh-option .value=${c} slot="options">${c.county_name}</lh-option>`)}
            </lh-select>`;
        }
    }

    private removeMember() {
        this.dispatchEvent(makeCustomEvent(EVENT.REMOVE_MEMBER, { member: this.idx }));
    }

    get xButton() {
        if (this.primary) return nothing;
        return html`<lh-icon-button icon="x" @click=${this.removeMember}></lh-icon-button> `;
    }

    get dobWarningBox() {
        if (!this.member.dob) return nothing;
        if (
            this.member.relation === RELATION.DEPENDENT &&
            isValidBirthday(this.member.dob) &&
            !isUnder26Years(this.member.dob)
        ) {
            return html`<div class="zip-warning">
                <div class="icon-div">
                    <lh-icon name="warning"></lh-icon>
                </div>
                <div>
                    <lh-typography size="sm">
                        All dependent children must be under 26 by plan start date.
                    </lh-typography>
                </div>
            </div>`;
        }
        if (isOver65Years(this.member.dob)) {
            return html`<div class="dob-warning">
                <div class="icon-div">
                    <lh-icon name="bell"></lh-icon>
                </div>
                <div>
                    <lh-typography size="sm">
                        You qualify for a <a href=${MEDICARE_URL}>Medicare plan</a>, which is typically more affordable
                        than individual plans.
                    </lh-typography>
                </div>
            </div>`;
        }
        return nothing;
    }

    render() {
        return !this.member
            ? nothing
            : html`
                  <div class="wrapper">
                      <div class="title">
                          <lh-typography type="heading" selector="h3" size=${this.isMobile ? 'xxl' : 'lg'}>
                              <strong>${this.memberTitle}</strong>
                          </lh-typography>
                          ${this.xButton}
                      </div>
                      <div class="inputs">
                          ${this.relationInput} ${this.dobInput} ${this.dobWarningBox} ${this.zipInput}
                          ${this.countyInput}
                          ${this.lastMember
                              ? html`
                                    <div class="zip-warning">
                                        <lh-typography size="sm">
                                            <strong>
                                                If you need to add more dependents, please call ${HELP_PHONE_NUMBER} for
                                                assistance.
                                            </strong>
                                        </lh-typography>
                                    </div>
                                `
                              : nothing}
                      </div>
                  </div>
                  ${this.errorToast}
              `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'rq-member-card': RateQuoteEstimate;
    }
}
